<template>
    <div class="relative">
        <!-- Поле ввода -->
        <div
            class="border border-gray-300 rounded-lg px-4 py-2 cursor-pointer hover:border-blue-400 transition-colors select-none"
            :class="{ 'border-blue-500 ring-2 ring-blue-200': isOpen }"
            @click="togglePicker"
        >
            <div class="flex items-center justify-between">
        <span class="text-gray-700">
          {{ displayValue }}
        </span>
                <CalendarIcon class="w-5 h-5 text-gray-400" />
            </div>
        </div>

        <div
            v-if="isOpen"
            class="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4 w-80"
        >
            <div class="flex justify-between items-center mb-4">
                <button
                    @click="prevMonth"
                    class="p-2 hover:bg-gray-100 rounded"
                >
                    <ChevronLeftIcon class="w-5 h-5" />
                </button>

                <span class="font-semibold text-gray-800 select-none">
                  {{ format(currentMonth, 'MMMM yyyy') }}
                </span>

                <button
                    @click="nextMonth"
                    class="p-2 hover:bg-gray-100 rounded"
                >
                    <ChevronRightIcon class="w-5 h-5" />
                </button>
            </div>

            <div class="grid grid-cols-7 gap-1 mb-2">
                <div
                    v-for="day in weekdays"
                    :key="day"
                    class="text-center text-sm font-medium text-gray-500 py-1 select-none"
                >
                    {{ day }}
                </div>
            </div>

            <div class="grid grid-cols-7 gap-1">
                <div
                    v-for="day in daysInMonth"
                    :key="day.date.toString()"
                    @click="selectDate(day.date)"
                    class="text-center p-2 rounded cursor-pointer text-sm select-none"
                    :class="getDayClasses(day)"
                >
                    {{ day.day }}
                </div>
            </div>

            <div class="flex justify-between mt-4 select-none">
                <button
                    @click="clearSelection"
                    class="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                    Очистить
                </button>
                <button
                    @click="applySelection"
                    class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                >
                    Применить
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, defineModel} from 'vue'
import {
    format,
    startOfMonth,
    endOfMonth,
    eachDayOfInterval,
    addMonths,
    addDays,
    subMonths,
    isSameMonth,
    isSameDay,
    isWithinInterval,
    isAfter,
    isBefore
} from 'date-fns'
import CalendarIcon from "@/components/UI/svg/CalendarIcon.vue";
import ChevronLeftIcon from "@/components/UI/svg/ChevronLeftIcon.vue";
import ChevronRightIcon from "@/components/UI/svg/ChevronRightIcon.vue";

const isOpen = ref(false)
const currentMonth = ref(new Date())
const startDate = defineModel('start-date')
const endDate = defineModel('end-date')
const tempEndDate = ref(null)

const weekdays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

const displayValue = computed(() => {
    if (startDate.value && endDate.value) {
        return `${format(startDate.value, 'dd.MM.yyyy')} - ${format(endDate.value, 'dd.MM.yyyy')}`
    } else if (startDate.value) {
        return `${format(startDate.value, 'dd.MM.yyyy')} - ...`
    }
    return 'Выберите диапазон дат'
})

const daysInMonth = computed(() => {
    const monthStart = startOfMonth(currentMonth.value)
    const monthEnd = endOfMonth(currentMonth.value)
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd })

    return days.map(date => ({
        date,
        day: format(date, 'd'),
        isCurrentMonth: isSameMonth(date, currentMonth.value),
        isSelected: isDateSelected(date),
        isInRange: isDateInRange(date),
        isStart: startDate.value && isSameDay(date, startDate.value),
        isEnd: endDate.value && isSameDay(date, endDate.value)
    }))
})

const isDateSelected = (date) => {
    if (!startDate.value || !endDate.value) return false
    return isWithinInterval(date, { start: startDate.value, end: endDate.value })
}

const isDateInRange = (date) => {
    if (!startDate.value || !tempEndDate.value) return false
    const start = startDate.value
    const end = tempEndDate.value

    if (isAfter(start, end)) {
        return isWithinInterval(date, { start: end, end: start })
    }
    return isWithinInterval(date, { start, end })
}

const getDayClasses = (day) => {
    return {
        'text-gray-400': !day.isCurrentMonth,
        'bg-blue-500 text-white': day.isStart || day.isEnd,
        'bg-blue-100': day.isInRange && !day.isStart && !day.isEnd,
        'hover:bg-gray-100': day.isCurrentMonth && !day.isSelected && !(day.isStart || day.isEnd),
        'font-semibold': day.isCurrentMonth,
        'bg-blue-200': day.date > startDate.value && day.date < endDate.value,
    }
}

const togglePicker = () => {
    isOpen.value = !isOpen.value
}

const prevMonth = () => {
    currentMonth.value = subMonths(currentMonth.value, 1)
}

const nextMonth = () => {
    currentMonth.value = addMonths(currentMonth.value, 1)
}

const selectDate = (date) => {
    if (!startDate.value) {
        startDate.value = date
        tempEndDate.value = date
        console.log(1)
    } else if (!endDate.value && isAfter(date, startDate.value)) {
        endDate.value = date
        tempEndDate.value = null
        console.log(2)

    } else if (!endDate.value && isBefore(date, startDate.value)) {
        endDate.value = startDate.value
        startDate.value = date
        tempEndDate.value = null
        console.log(3)

    } else if(!endDate.value && isSameDay(date, startDate.value))  {
        clearSelection()
        console.log(4)

    }
    else {
        startDate.value = date
        endDate.value = null
        tempEndDate.value = date
        console.log(5)

    }
}

const clearSelection = () => {
    startDate.value = startOfMonth(currentMonth.value)
    endDate.value = endOfMonth(currentMonth.value)
    tempEndDate.value = null
}

const applySelection = () => {
    if (startDate.value && !endDate.value) {
        endDate.value = addDays(startDate.value, 1)
    }
    isOpen.value = false
}

const handleClickOutside = (event) => {
    const picker = event.target.closest('.relative')
    if (!picker && isOpen.value) {
        applySelection()
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
})
</script>